import { useCallback } from 'react';
import groupBy from 'lodash.groupby';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { GuidService } from 'ui-components/services/guid-service';

import { createAdjustItemsRequestItem } from './helpers/create-adjust-items-request-item';
import { generateSplitShipEntityChecksum } from './helpers/generate-split-ship-entity-checksum';
import { adjustItemsSync } from './split-ship-api-client';
import { usePusherEvent } from '../../hooks/use-pusher-event';
import { FULFILLMENT_PLAN_ITEM_ADJUSTMENT } from '../../services/pusher-service/pusher-contract-messages';
import * as CswActions from '../../store/csw/csw.actions';
import * as EntitiesActions from '../../store/entities/entities.actions';
import * as OrderDetailsActions from '../../store/orders/order-details/orders.details.actions';
import { silentRequestGetOrders } from '../../store/orders/orders.actions';

const getChangelist = (initialTableData, tableData) => {
  const itemsGroupedByFulfillmentPlanItemId = groupBy(tableData, 'fulfillmentPlanItemId');

  const changelist = {};

  const createChangelistUpdater = (fulfillmentPlanItemId, originalFpId) => {
    const addChange = (fulfillmentPlanId, quantity, trueFlagForZeroQuantityMovements) => {
      if (!changelist[fulfillmentPlanId]) {
        changelist[fulfillmentPlanId] = {};
      }
      changelist[fulfillmentPlanId][fulfillmentPlanItemId] = createAdjustItemsRequestItem(
        originalFpId,
        fulfillmentPlanItemId,
        quantity,
        trueFlagForZeroQuantityMovements,
      );
    };

    return {
      addRemoveItemChange: () => addChange(originalFpId, 0),
      addUpdateItemChange: addChange,
    };
  };

  initialTableData.forEach(
    ({ fulfillmentPlanId: initialFulfillmentPlanId, fulfillmentPlanItemId: initialFulfillmentPlanItemId, quantity: initialQuantity }) => {
      const { addRemoveItemChange, addUpdateItemChange } = createChangelistUpdater(initialFulfillmentPlanItemId, initialFulfillmentPlanId);

      const updatedItems = itemsGroupedByFulfillmentPlanItemId[initialFulfillmentPlanItemId] || [];
      const itemIsNotInOriginalFulfillmentPlan = !updatedItems.some((x) => x.fulfillmentPlanId === initialFulfillmentPlanId);

      if (itemIsNotInOriginalFulfillmentPlan) {
        addRemoveItemChange();
      }

      updatedItems.forEach((item) => {
        const fulfillmentPlanChanged = item.fulfillmentPlanId !== initialFulfillmentPlanId;
        const quantityChanged = item.quantity !== initialQuantity;
        if (fulfillmentPlanChanged || quantityChanged) {
          addUpdateItemChange(item.fulfillmentPlanId, item.quantity, true);
        }
      });
    },
  );

  return changelist;
};

export const useSaveSplitShip = ({ closeModal, fulfillmentPlans, initialTableData, salesOrderId, tableData }) => {
  const [saveJobId, setSaveJobId] = useAsyncSafeState('');
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [saveFailed, setSaveFailed] = useAsyncSafeState(false);

  const dispatch = useDispatch();

  const onSave = useCallback(() => {
    dispatch(CswActions.syncCswToEntities());
    dispatch(EntitiesActions.bulkRequestEntities({ salesOrderIds: [salesOrderId] }));
    dispatch(OrderDetailsActions.requestOrderDetails(false, false, true));
    dispatch(silentRequestGetOrders());
    setSaveJobId('');
    setIsSaving(false);
  }, [dispatch, salesOrderId, setIsSaving, setSaveJobId]);

  const onSaveSuccess = useCallback(() => {
    onSave();
    closeModal();
  }, [closeModal, onSave]);

  const onSaveFail = useCallback(() => {
    onSave();
    setSaveFailed(true);
  }, [onSave, setSaveFailed]);

  const save = useCallback(() => {
    setIsSaving(true);
    setSaveFailed(false);

    const changes = getChangelist(initialTableData, tableData);

    // no changes should just close the modal and not make request
    if (Object.keys(changes).length === 0) {
      closeModal();
      return;
    }

    const requestFulfillmentPlans = fulfillmentPlans.reduce((acc, { fulfillmentPlanId, isNew }) => {
      const changesForFp = Object.values(changes[fulfillmentPlanId] || {});

      if (isNew || changesForFp.length > 0) {
        acc.push({
          fulfillmentPlanId: isNew ? null : fulfillmentPlanId,
          items: changesForFp,
          requestId: GuidService.newGuid(),
        });
      }

      return acc;
    }, []);

    const request = {
      fulfillmentPlans: requestFulfillmentPlans,
      salesOrderId,
      entityChecksum: generateSplitShipEntityChecksum(initialTableData),
    };

    adjustItemsSync(request).then(onSaveSuccess).catch(onSaveFail);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsSaving, setSaveFailed, initialTableData, tableData, fulfillmentPlans, salesOrderId, onSaveSuccess, onSaveFail, setSaveJobId]);

  const onSavePusherMessageReceived = useCallback(
    (pusherMessage) => {
      if (pusherMessage.messageGuid === saveJobId) {
        if (pusherMessage.data.success) {
          onSaveSuccess();
        } else {
          onSaveFail();
        }
      }
    },
    [onSaveFail, onSaveSuccess, saveJobId],
  );

  usePusherEvent(FULFILLMENT_PLAN_ITEM_ADJUSTMENT, onSavePusherMessageReceived);

  return { save, isSaving, saveFailed };
};
